import { Hash } from 'lucide-react';
import { z } from 'zod';
import { NumberFieldInput, NumberFieldView } from '../renderers/fields/number/number-field';
import type { FieldDefinition, NumberFieldSchema } from '../types';

export const NumberDefinition: FieldDefinition<NumberFieldSchema, number | '' | null | undefined> =
  {
    type: 'number',
    toolboxLabel: 'Number Input',
    category: 'Standard Inputs',
    description: 'Strictly numeric input with optional min/max/step validation.',
    icon: Hash,

    createDefault: (id) => ({
      id,
      type: 'number',
      name: `number_${id}`,
      label: 'Number Field',
      required: false,
      placeholder: '0',
    }),

    configSchema: [
      { id: 'label', type: 'text', name: 'label', label: 'Field Label', required: true },
      { id: 'name', type: 'text', name: 'name', label: 'Data Key (ID)', required: true },
      {
        id: 'placeholder',
        type: 'text',
        name: 'placeholder',
        label: 'Placeholder',
        required: false,
      },
      {
        id: 'required',
        type: 'checkbox',
        name: 'required',
        label: 'Required Field',
        required: false,
      },
      { id: 'min', type: 'number', name: 'min', label: 'Min Value', required: false },
      { id: 'max', type: 'number', name: 'max', label: 'Max Value', required: false },
      { id: 'step', type: 'number', name: 'step', label: 'Step', required: false },
    ],

    buildValidation: (field) => {
      // If required, it MUST be a number (not '')
      if (field.required) {
        // Create a specific number schema for validation
        let numSchema = z.number({ error: `${field.label} is required` });

        if (field.min !== undefined) {
          numSchema = numSchema.min(field.min, `${field.label} must be at least ${field.min}`);
        }
        if (field.max !== undefined) {
          numSchema = numSchema.max(field.max, `${field.label} cannot exceed ${field.max}`);
        }

        return numSchema;
      }

      // If not required, it can be a number (with constraints) or ''
      let numSchema = z.number();
      if (field.min !== undefined) {
        numSchema = numSchema.min(field.min, `${field.label} must be at least ${field.min}`);
      }
      if (field.max !== undefined) {
        numSchema = numSchema.max(field.max, `${field.label} cannot exceed ${field.max}`);
      }

      return z.union([numSchema, z.literal('')]).optional();
    },

    editComponent: NumberFieldInput,
    viewComponent: NumberFieldView,
  };
